/**
 * Getting back to the staff site from the member portal.
 *
 * The staff site and the member portal share this browser's localStorage. A
 * member sign-in (storeMemberSession) overwrites `refresh_token` and
 * `active_cbo_id`, which the staff session also uses, so a staff user who
 * opened the member portal had no way back: the staff site came up in the
 * member's org and lost its refresh token at the next expiry.
 *
 * Before a member sign-in overwrites them, rememberStaffSession() keeps the
 * staff values. returnToStaffSite() ends the member session, puts them back,
 * and opens the staff site, whose session is otherwise untouched.
 *
 * Not used while a super admin views as a member (TRUS-747): the
 * impersonation banner's Exit handles that.
 */
import { getAuthToken } from '@/lib/authFetch';
import { isImpersonating } from '@/lib/impersonation';

const KEY = 'staff_return';

interface StaffReturn {
  cboId: string | null;
  refreshToken: string | null;
}

function read(): StaffReturn | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StaffReturn) : null;
  } catch {
    return null;
  }
}

/** Call before a member sign-in writes its session. No-op without a staff session. */
export function rememberStaffSession(): void {
  if (!getAuthToken() || isImpersonating()) return;
  // A second member sign-in must not overwrite the staff values with the
  // first member's.
  if (read()) return;
  localStorage.setItem(
    KEY,
    JSON.stringify({
      cboId: localStorage.getItem('active_cbo_id'),
      refreshToken: localStorage.getItem('refresh_token'),
    }),
  );
}

/** True when this browser also holds a staff session to go back to. */
export function hasStaffSession(): boolean {
  return !!getAuthToken() && !isImpersonating();
}

/** Forget the saved staff values (staff sign-out, or after restoring them). */
export function clearStaffReturn(): void {
  localStorage.removeItem(KEY);
}

/**
 * End the member session and open the staff site with its own org and
 * refresh token back in place. `endMemberSession` is logoutMember, passed in
 * to keep this module free of the member auth module.
 */
export function returnToStaffSite(endMemberSession: () => void): void {
  const saved = read();
  endMemberSession();
  if (saved) {
    if (saved.cboId) localStorage.setItem('active_cbo_id', saved.cboId);
    else localStorage.removeItem('active_cbo_id');
    if (saved.refreshToken) localStorage.setItem('refresh_token', saved.refreshToken);
  }
  clearStaffReturn();
  window.location.href = '/';
}
